import React from 'react';

/**
 * Digikoç bandı — sayfanın hero'dan sonraki ikinci anı.
 *
 * Yapı (masaüstü): tam genişlik başlık + tek satır lead; altında İKİ EŞİT SÜTUN,
 * TEK SATIRDA — solda Digikoç Yorumu (yapay zekâ yorumu, maskot konuşuyor), sağda
 * Sana Özel Koçluk (güçlü / zayıf konu dağılımı). Başlıklar, açıklamalar ve görsel
 * bloklar aynı yükseklikten başlar. Altta, ince bir çizginin ardında, daha küçük ve
 * daha sessiz 2. kademe: Podcastler + Sınava Doğru. Hiyerarşi etiketle değil
 * boyut ve yerle kuruluyor. Dar ekranda tek sütun: başlık → Yorum → Koçluk → 2. kademe.
 *
 * İkisi de uygulamada gerçekten var:
 *   • Digikoç Yorumu — student/Performance.tsx: yapay zekâ, haftalık + aylık +
 *     tüm zamanlar performansını Türkçe yorumluyor (GET /student/performance/insight,
 *     akış olarak gelir), yorumun altında zayıf konular için kitap bölümü bağlantıları.
 *   • Sana Özel Koçluk — student/Digikoc.tsx: çözülen sorulardan "Güçlü Konuların" /
 *     "Geliştirmen Gereken" ayrımı; konu başına çözümlü video önerisi.
 *
 * Yorum ve konu listesi ÖRNEKTİR (uydurma öğrenci, uydurma sayılar) ve öyle
 * etiketlenir; gerçek bir öğrencinin verisi değil. Hiçbir şey canlı yazılmaz ya da
 * animasyonla oynatılmaz — sayfanın tek animasyonu yükleme anındaki robot/balon.
 *
 * Renk: uygulamanın kendi güçlü/zayıf rengi (#10b981 / #e5599b) yalnızca işaret ve
 * çubuk dolgusu olarak; metin her zaman koyu mürekkeple (pembe metin AA'yı geçmez).
 */

interface Topic {
  name: string;
  pct: number;
}

// Gerçekçi LGS / TYT konu adları; yüzdeler örnek.
const STRONG: Topic[] = [
  { name: 'Üslü İfadeler', pct: 92 },
  { name: 'Fiilimsiler', pct: 88 },
  { name: 'Oran Orantı', pct: 85 },
];
const WEAK: Topic[] = [
  { name: 'Paragrafta Anlam', pct: 41 },
  { name: 'Basit Olasılık', pct: 47 },
  { name: 'Eşitsizlikler', pct: 52 },
];

const PlayDot: React.FC = () => (
  <svg className="lp-topic__play" viewBox="0 0 20 20" aria-hidden focusable="false">
    <circle cx="10" cy="10" r="10" />
    <path d="M8 6.2v7.6L14 10z" />
  </svg>
);

const TopicList: React.FC<{ topics: Topic[]; tone: 'strong' | 'weak' }> = ({ topics, tone }) => (
  <ul className={`lp-topics lp-topics--${tone}`}>
    {topics.map((t) => (
      <li key={t.name} className="lp-topic">
        <span className="lp-topic__name">
          {t.name}
          {tone === 'weak' && <PlayDot />}
        </span>
        <span className="lp-topic__pct">%{t.pct}</span>
        <span className="lp-topic__bar" aria-hidden>
          <i style={{ width: `${t.pct}%` }} />
        </span>
      </li>
    ))}
  </ul>
);

const DigikocBand: React.FC = () => (
  <section className="lp-dk" aria-labelledby="lp-dk-title">
    <div className="lp-wrap lp-dk__inner">
      <div className="lp-dk__head">
        <h2 id="lp-dk-title" className="lp-h2">
          Digikoç neye çalışacağını söyler.
        </h2>
        <p className="lp-dk__lead">
          Yapay zekâ çözdüğün sorulara bakar, ilerlemeni yorumlar ve çalışman gereken konuları
          gösterir.
        </p>
      </div>

      {/* 1. kademe: iki eşit sütun, tek satır. */}
      <div className="lp-dk__features">
        {/* Digikoç Yorumu: balon + altında, sol altta konuşan maskot (hero ile aynı balon dili). */}
        <div className="lp-dk__yorum">
          <h3 className="lp-dk__h3">Digikoç Yorumu</h3>
          <p className="lp-dk__sub">Haftanı ve ayını değerlendirir, sıradaki adımı söyler.</p>
          <div className="lp-dk__chat">
            <div className="lp-bubble lp-dk__bubble">
              <div className="lp-bubble__bg" aria-hidden>
                <svg className="lp-bubble__tail" viewBox="0 0 56 44" focusable="false">
                  <path d="M56 3 C38 4 15 14 1 38 C20 40 40 38 56 31 Z" />
                </svg>
              </div>
              <p className="lp-dk__say">
                Merhaba Elif! Bu hafta 148 soru çözdün ve doğru oranın %72’ye çıktı; bu ayın
                ortalamasının (%64) üstündesin. Sıradaki hedefin Paragrafta Anlam: önce çözümlü
                videoyu izle, sonra 10 soru daha çöz.
              </p>
              <p className="lp-dk__eg">Örnek yorum</p>
            </div>
            <img
              className="lp-dk__mascot"
              src="/a-digikoc-mascot.png"
              alt="Digikoç maskotu: elinde düdük tutan koç robot, çevresinde kâğıt uçak ve yıldızlar uçuşuyor."
              width={771}
              height={771}
              loading="lazy"
              decoding="async"
              draggable={false}
            />
          </div>
        </div>

        {/* Sana Özel Koçluk: güçlü / geliştirmen gereken ayrımı. */}
        <div className="lp-dk__koc">
          <h3 className="lp-dk__h3">Sana Özel Koçluk</h3>
          <p className="lp-dk__sub">Güçlü ve zayıf konularını ayırır, çözümlü video önerir.</p>
          <div className="lp-dk__panel" role="group" aria-label="Örnek konu dağılımı">
            <div className="lp-split">
              <div>
                <p className="lp-split__head lp-split__head--strong">
                  <span className="lp-split__dot" aria-hidden />
                  Güçlü Konuların
                </p>
                <TopicList topics={STRONG} tone="strong" />
              </div>
              <div>
                <p className="lp-split__head lp-split__head--weak">
                  <span className="lp-split__dot" aria-hidden />
                  Geliştirmen Gereken
                </p>
                <TopicList topics={WEAK} tone="weak" />
              </div>
            </div>
            <p className="lp-dk__eg">Örnek konu dağılımı</p>
          </div>
        </div>
      </div>

      {/* 2. kademe — daha küçük, aşağıda, daha sessiz. */}
      <div className="lp-dk__diger">
        <div>
          <h3>Podcastler</h3>
          <p>Derslere göre ayrılmış Digikoç podcastleri.</p>
        </div>
        <div>
          <h3>Sınava Doğru</h3>
          <p>Sınava hazırlık için video serisi.</p>
        </div>
      </div>
    </div>
  </section>
);

export default DigikocBand;
